"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Brain,
  FileText,
  Calendar,
  Building2,
  Menu,
  Megaphone,
  Plus,
  X,
  Lightbulb,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NewItemModal } from "@/components/NewItemModal";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

type NavItem = { href: string; label: string; icon: typeof LayoutDashboard };

const navItems: NavItem[] = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/clients", label: "Clients", icon: Building2 },
  { href: "/projects", label: "Projects", icon: FolderKanban },
  { href: "/tasks", label: "Tasks", icon: CheckSquare },
  { href: "/ideas", label: "Ideas", icon: Lightbulb },
  { href: "/office", label: "Office", icon: Building2 },
  { href: "/memory", label: "Memory", icon: Brain },
  { href: "/documents", label: "Documents", icon: FileText },
  { href: "/calendar", label: "Calendar", icon: Calendar },
  { href: "/social", label: "Social", icon: Megaphone },
];

function UserFooter() {
  const { signOut } = useAuthActions();
  const currentUser = useQuery(api.ideas.currentUser);

  const displayName = currentUser?.name ?? currentUser?.email ?? "You";
  const initials = displayName
    .split(" ")
    .map((p: string) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex items-center gap-2 border-t border-sidebar-border px-4 py-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium leading-tight">{displayName}</div>
        {currentUser?.role && (
          <div className="text-[10px] leading-tight text-muted-foreground capitalize">
            {currentUser.role}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <ThemeToggle />
        <button
          type="button"
          onClick={() => void signOut()}
          title="Sign out"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function SidebarContent({
  onNavigate,
  onOpenNew,
}: {
  onNavigate?: () => void;
  onOpenNew: () => void;
}) {
  const pathname = usePathname();

  return (
    <>
      <div className="flex items-center gap-3 border-b border-sidebar-border px-5 py-5">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 shadow-sm"
          style={{ backgroundImage: "linear-gradient(135deg, #7336d6 0%, #b266ff 100%)" }}
        >
          B
        </div>
        <div className="min-w-0">
          <div className="text-base font-semibold leading-tight tracking-tight md:text-sm">
            Backchannel
          </div>
          <div className="mt-0.5 text-sm leading-tight text-muted-foreground md:text-xs">
            Mission Control
          </div>
        </div>
      </div>

      <div className="px-3 pt-4">
        <button
          type="button"
          onClick={onOpenNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 px-3 py-2 text-sm font-medium text-white shadow-sm transition-all hover:from-indigo-400 hover:to-violet-400 hover:shadow"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            item.href === "/"
              ? pathname === "/"
              : pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[15px] transition-colors md:rounded-lg md:py-2 md:text-sm",
                isActive
                  ? "border-primary/15 bg-primary/10 text-sidebar-foreground font-medium shadow-sm"
                  : "border-transparent text-sidebar-foreground/75 hover:border-sidebar-border hover:bg-sidebar-accent/80 hover:text-sidebar-foreground",
              )}
            >
              <Icon
                className={cn(
                  "w-4 h-4 flex-shrink-0",
                  isActive ? "text-primary" : "text-muted-foreground",
                )}
              />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <UserFooter />
    </>
  );
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-sidebar-border bg-background/92 px-4 py-3.5 backdrop-blur-sm md:hidden">
        <Link href="/" className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-sm font-semibold text-white shadow-sm"
            style={{ backgroundImage: "linear-gradient(135deg, #7336d6 0%, #b266ff 100%)" }}
          >
            B
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold leading-tight tracking-tight text-foreground">
              Backchannel
            </div>
            <div className="text-sm leading-tight text-muted-foreground">
              Mission Control
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle className="h-9 w-9" />
          <button
            type="button"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMobileOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <aside className="fixed left-0 top-0 z-40 hidden h-full w-60 flex-col border-r border-sidebar-border bg-sidebar/96 text-sidebar-foreground shadow-[1px_0_0_rgba(0,0,0,0.02)] backdrop-blur-sm md:flex">
        <SidebarContent onOpenNew={() => setNewOpen(true)} />
      </aside>

      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] transition-opacity md:hidden",
          mobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={() => setMobileOpen(false)}
      />

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[86vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-end border-b border-sidebar-border px-4 py-3">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <SidebarContent
          onNavigate={() => setMobileOpen(false)}
          onOpenNew={() => {
            setMobileOpen(false);
            setNewOpen(true);
          }}
        />
      </aside>

      <NewItemModal open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}
